import { Button, Flex, Typography } from 'antd';

import {
  listingActionAriaLabel,
  renderListingToolbarActionItem,
  resolveListingActionTestId,
  wrapListingActionTooltip,
} from './listingActionRender';

import type { ListingToolbarActionItem } from './listingToolbarConfig';
import type {
  ButtonColorType,
  ButtonType,
  ButtonVariantType,
} from 'antd/es/button';
import type React from 'react';

type FilteredListingHeaderDeclarativeActionItem = {
  id: string;
  /**
   * Explicit semantic test id for Cypress; falls back to `dataTestId`, then
   * `rm-header-action-{id}`. Prefer `testId` on header actions.
   */
  testId?: string;
  /** Alias of `testId` (toolbar naming); either works. */
  dataTestId?: string;
  label: React.ReactNode;
  /** Optional Tooltip wrap (icon-only or permission hints). */
  tooltip?: string;
  icon?: React.ReactNode;
  color?: ButtonColorType;
  variant?: ButtonVariantType;
  type?: ButtonType;
  danger?: boolean;
  loading?: boolean;
  onClick: () => void;
  disabled?: boolean;
};

export type FilteredListingHeaderCustomActionItem = {
  id: string;
  kind: 'custom';
  render: () => React.ReactNode;
};

export type FilteredListingHeaderActionItem =
  | FilteredListingHeaderDeclarativeActionItem
  | FilteredListingHeaderCustomActionItem
  | ListingToolbarActionItem;

function renderDeclarativeActions(items: FilteredListingHeaderActionItem[]) {
  return items.map((item) => {
    if ('kind' in item) {
      return renderListingToolbarActionItem(item, { headerFallback: true });
    }

    const testId = resolveListingActionTestId(item, { headerFallback: true });
    const button = (
      <Button
        data-testid={testId}
        type={item.type}
        color={item.color}
        variant={item.variant}
        danger={item.danger}
        loading={item.loading}
        icon={item.icon}
        aria-label={listingActionAriaLabel(item.label)}
        onClick={item.onClick}
        disabled={item.disabled}
      >
        {item.label}
      </Button>
    );

    return wrapListingActionTooltip(item.id, button, item.tooltip);
  });
}

export type FilteredListingPageHeaderProps = {
  title?: string;
  subtitle: string;
  /** Optional content next to the title (e.g. screen-specific badges). */
  titleAddon?: React.ReactNode;
  /** Rendered in the header action row before `actionItems` (e.g. inline filters). */
  actionPrefix?: React.ReactNode;
  /** Context rendered above the right-side action row. */
  actionHeader?: React.ReactNode;
  actionItems?: FilteredListingHeaderActionItem[];
  children?: React.ReactNode;
};

export function FilteredListingPageHeader({
  title,
  subtitle,
  titleAddon,
  actionPrefix,
  actionHeader,
  actionItems,
  children,
}: FilteredListingPageHeaderProps) {
  const actionCluster = actionItems?.length
    ? renderDeclarativeActions(actionItems)
    : null;
  const hasActionRow = actionPrefix != null || actionCluster != null;
  const hasActionColumn = actionHeader != null || hasActionRow;

  return (
    <Flex vertical gap={0}>
      <Flex vertical gap={0}>
        <Flex justify="space-between" align="center" wrap gap={12}>
          <Flex vertical gap={4}>
            {title != null || titleAddon != null ? (
              <Flex align="center" wrap>
                {title != null ? (
                  <Typography.Title level={3} ellipsis>
                    {title}
                  </Typography.Title>
                ) : null}
                {titleAddon}
              </Flex>
            ) : null}
            <Typography.Text type="secondary">{subtitle}</Typography.Text>
          </Flex>
          {hasActionColumn ? (
            <Flex vertical align="stretch" gap={8}>
              {actionHeader}
              {hasActionRow ? (
                <Flex align="center" wrap justify="flex-end" gap={6}>
                  {actionPrefix}
                  {actionCluster}
                </Flex>
              ) : null}
            </Flex>
          ) : null}
        </Flex>

        {children}
      </Flex>
    </Flex>
  );
}
